import { Dispatch } from 'redux';
import find from 'lodash/find';
import startOfDay from 'date-fns/startOfDay';
import { requestInFlight, requestSuccess } from '..';
import { getLessonPlan } from 'api/lesson-planner';
import { getProductConfig, updateProductConfig } from 'api/product-configs';
import {
  getTeachingOrders,
  updateTeachingOrder,
  removeTeachingOrder,
  createTeachingOrder,
  getTeachingOrderById
} from 'api/teaching-order';
import { generateTeachingOrderName, fillDayGaps } from 'util/lesson-plan-util';
import { batch } from 'react-redux';
import { fetchLessonPlan } from './lesson-planner';
import { setLessonPlannerNavbarAttribute } from 'state/modules/lessonPlanner/thunks';
import { formatFullDate } from 'util/date-util';

export const createCustomTeachingOrder = (lessonIds: number[], emptyDates?, active = true) => {
  return async (dispatch, getState) => {
    dispatch(requestInFlight());
    const {
      app: { currentWorkbook },
      entity: { teachingOrder },
      user: {
        me: { firstName, lastName }
      }
    } = await getState();

    const name = generateTeachingOrderName(
      teachingOrder,
      `${firstName} ${lastName} ${currentWorkbook.title} Teaching Order`
    );

    const [{ productCurriculumId }] = currentWorkbook.products;
    const params = {
      name,
      productCurriculumId,
      custom: true,
      lessons: lessonIds
    };
    try {
      return await batch(async () => {
        const { data: customTeachingOrder } = await createTeachingOrder(params);
        const { data: productConfig } = await updateProductConfig(productCurriculumId, {
          teachingOrderId: customTeachingOrder.id,
          custom: true,
          active,
          emptyDates
        });
        await dispatch(fetchLessonPlan());
        await dispatch(fetchTeachingOrder());
        await dispatch(requestSuccess({ productConfig }));
        return await dispatch(requestSuccess({ customTeachingOrder }));
      });
    } catch (e) {
      dispatch(requestSuccess({}));
    }
  };
};

export const fetchTeachingOrder = () => {
  return async (dispatch, getState) => {
    dispatch(requestInFlight());

    const {
      app: { currentWorkbook },
      entity
    } = getState();
    const params = {
      productCurriculumId: currentWorkbook?.products[0].productCurriculumId
    };

    try {
      const { data } = await getTeachingOrders(params);

      const currentTeachingOrder = find(
        data,
        ({ id, name }) => id === entity.lessonPlan?.id && name === entity.lessonPlan?.name
      );

      await batch(() => {
        dispatch(setLessonPlannerNavbarAttribute('teachingOrder', currentTeachingOrder));
        return dispatch(requestSuccess({ teachingOrder: data }));
      });
    } catch (e) {
      dispatch(requestSuccess({}));
    }
  };
};

export const updateTeachingOrderLessonsAndFetchLessonPlan = (
  teachingOrderLessonIds,
  shouldFetchLessonPlan = true
) => {
  return async (dispatch, getState) => {
    dispatch(requestInFlight());
    const {
      entity: { lessonPlan }
    } = await getState();

    const params = { custom: lessonPlan.custom };
    const data = { teachingOrderLessonIds };

    try {
      const { data: updatedTeachingOrder } = await updateTeachingOrder(lessonPlan.id, {
        params,
        data
      });

      batch(async () => {
        shouldFetchLessonPlan && (await dispatch(fetchLessonPlan()));
        return await dispatch(requestSuccess({ updatedTeachingOrder }));
      });
    } catch (e) {
      dispatch(requestSuccess({}));
    }
  };
};

export const getUserLessonPlans = teachingOrder => {
  return async (dispatch: Dispatch, getState: () => any) => {
    dispatch(requestInFlight());
    const {
      app: { locale }
    } = await getState();

    const params = { custom: teachingOrder.custom };

    try {
      const { data: lessonPlan } = await getLessonPlan({
        ...params,
        teachingOrderId: teachingOrder.id
      });

      const { data: maybeActiveTeachingOrder } = await getTeachingOrderById(
        teachingOrder.id,
        params
      );

      const isActive = maybeActiveTeachingOrder.active;
      return await dispatch(
        requestSuccess({
          lessonPlan: { ...lessonPlan, lessons: fillDayGaps(lessonPlan.lessons, locale), isActive }
        })
      );
    } catch (e) {
      dispatch(requestSuccess({}));
    }
  };
};

export const updateActiveTeachingOrder = teachingOrder => {
  return async (dispatch, getState) => {
    dispatch(requestInFlight());

    const {
      app: { currentWorkbook }
    } = getState();

    try {
      const [{ productCurriculumId }] = currentWorkbook.products;
      const { data: productConfig } = await updateProductConfig(productCurriculumId, {
        teachingOrderId: teachingOrder.id,
        custom: teachingOrder.custom,
        active: true
      });

      await batch(async () => {
        await dispatch(fetchLessonPlan());
        await dispatch(fetchTeachingOrder());
        await dispatch(requestSuccess({ productConfig }));
      });
    } catch (e) {
      dispatch(requestSuccess({}));
    }
  };
};

export const updateStartDate = startDate => {
  return async (dispatch, getState) => {
    dispatch(requestInFlight());

    const {
      app: { currentWorkbook }
    } = getState();

    try {
      const [{ productCurriculumId }] = currentWorkbook.products;
      const { data: productConfig } = await updateProductConfig(productCurriculumId, {
        startDate: formatFullDate(startOfDay(startDate))
      });

      await batch(async () => {
        await dispatch(fetchLessonPlan());
        await dispatch(fetchTeachingOrder());
        await dispatch(requestSuccess({ productConfig }));
      });
    } catch (e) {
      dispatch(requestSuccess({}));
    }
  };
};

export const getProductConfiguration = () => {
  return async (dispatch, getState) => {
    dispatch(requestInFlight());

    const {
      app: { currentWorkbook }
    } = getState();

    try {
      const [{ productCurriculumId }] = currentWorkbook.products;
      const { data: productConfig } = await getProductConfig(productCurriculumId);

      await dispatch(requestSuccess({ productConfig }));
    } catch (e) {
      dispatch(requestSuccess({}));
    }
  };
};

export const updateInstructionDates = instructionDates => {
  return async (dispatch, getState) => {
    dispatch(requestInFlight());

    const {
      app: { currentWorkbook }
    } = getState();

    try {
      const [{ productCurriculumId }] = currentWorkbook.products;
      const { data: productConfig } = await updateProductConfig(productCurriculumId, {
        instructionDates
      });

      await batch(async () => {
        await dispatch(fetchLessonPlan());
        await dispatch(fetchTeachingOrder());
        await dispatch(requestSuccess({ productConfig }));
      });
    } catch (e) {
      dispatch(requestSuccess({}));
    }
  };
};

export const updateEmptyDates = emptyDates => {
  return async (dispatch, getState) => {
    dispatch(requestInFlight());

    const {
      app: { currentWorkbook }
    } = getState();

    try {
      const [{ productCurriculumId }] = currentWorkbook.products;
      const { data: productConfig } = await updateProductConfig(productCurriculumId, {
        emptyDates
      });

      await batch(async () => {
        await dispatch(fetchLessonPlan());
        await dispatch(fetchTeachingOrder());
        await dispatch(requestSuccess({ productConfig }));
      });
    } catch (e) {
      dispatch(requestSuccess({}));
    }
  };
};

export const renameTeachingOrder = (teachingOrderId, custom, name) => {
  return async dispatch => {
    dispatch(requestInFlight());

    const params = { custom };
    const data = { name };

    try {
      await batch(async () => {
        const { data: updatedTeachingOrder } = await updateTeachingOrder(teachingOrderId, {
          params,
          data
        });
        await dispatch(fetchLessonPlan());
        await dispatch(fetchTeachingOrder());
        await dispatch(requestSuccess({ updatedTeachingOrder }));
      });
    } catch (e) {
      dispatch(requestSuccess({}));
    }
  };
};

export const updateAndCombineTeachingOrder = (
  teachingOrderId,
  teachingOrderLessonIds,
  emptyDates
) => {
  return async (dispatch, getState) => {
    dispatch(requestInFlight());

    const {
      app: { currentWorkbook },
      entity: { lessonPlan }
    } = await getState();

    const params = { custom: lessonPlan.custom };
    const data = { teachingOrderLessonIds };

    try {
      await batch(async () => {
        const [{ productCurriculumId }] = currentWorkbook.products;
        let productConfig;

        if (emptyDates && emptyDates.length) {
          ({ data: productConfig } = await updateProductConfig(productCurriculumId, {
            emptyDates
          }));
        }
        const { data: updatedTeachingOrder } = await updateTeachingOrder(teachingOrderId, {
          params,
          data
        });
        await dispatch(fetchLessonPlan());
        if (productConfig) await dispatch(requestSuccess({ productConfig }));
        return await dispatch(requestSuccess({ updatedTeachingOrder }));
      });
    } catch (e) {
      dispatch(requestSuccess({}));
    }
  };
};

export const deleteTeachingOrder = teachingOrder => {
  return async (dispatch, getState) => {
    dispatch(requestInFlight());
    const {
      entity: { teachingOrder: teachingOrders }
    } = getState();

    const param = {
      custom: teachingOrder.custom
    };
    const defaultTeachingOrder = teachingOrders.find(to => to.default);

    try {
      await batch(async () => {
        await removeTeachingOrder(teachingOrder.id, param);
        if (teachingOrder.active) {
          await dispatch(updateActiveTeachingOrder(defaultTeachingOrder));
        } else {
          await dispatch(fetchTeachingOrder());
        }

        return await dispatch(requestSuccess({}));
      });
    } catch (e) {
      dispatch(requestSuccess({}));
    }
  };
};
